import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

interface RecaptchaV2Props {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  invisible?: boolean;
}

export interface RecaptchaV2Handle {
  execute: () => void;
  reset: () => void;
}

const RecaptchaV2 = forwardRef<RecaptchaV2Handle, RecaptchaV2Props>(({ 
  siteKey, 
  onVerify, 
  onExpire,
  invisible = true 
}, ref) => {
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Keep callback refs in sync without triggering re-renders
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;

  useEffect(() => {
    // Already loaded globally (another instance or previous mount)
    if (window.grecaptcha?.render) {
      setIsScriptLoaded(true);
      return;
    }

    // Script tag already exists (another instance is loading it)
    const existing = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existing) {
      window.onRecaptchaLoad = () => setIsScriptLoaded(true);
      return;
    }

    window.onRecaptchaLoad = () => setIsScriptLoaded(true);

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Don't remove the script on unmount -- other instances may need it,
    // and grecaptcha state persists globally regardless
  }, []);

  useEffect(() => {
    if (!isScriptLoaded || !recaptchaRef.current || widgetIdRef.current !== null) {
      return;
    }

    try {
      widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: siteKey,
        size: invisible ? 'invisible' : 'normal',
        callback: (token: string) => onVerifyRef.current(token),
        'expired-callback': () => onExpireRef.current?.()
      });
    } catch {
      // Container was already rendered (e.g. StrictMode double-mount or fast remount).
      // Reset the existing widget and try once more.
      try {
        window.grecaptcha.reset();
        widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: siteKey,
          size: invisible ? 'invisible' : 'normal',
          callback: (token: string) => onVerifyRef.current(token),
          'expired-callback': () => onExpireRef.current?.()
        });
      } catch {
        // Silently swallow -- the existing widget still functions
      }
    }

    return () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch {
          // ignore -- element may already be gone from DOM
        }
        widgetIdRef.current = null;
      }
    };
  }, [isScriptLoaded, siteKey, invisible]);

  const execute = useCallback(() => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.execute(widgetIdRef.current);
    }
  }, []);

  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  }, []);

  // Expose execute and reset methods via ref
  useImperativeHandle(ref, () => ({
    execute,
    reset
  }), [execute, reset]);

  return <div ref={recaptchaRef} />;
});

RecaptchaV2.displayName = 'RecaptchaV2';

export default RecaptchaV2;
